import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  PolarAngleAxis,
  PolarGrid,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AppShell } from "@/components/layout/AppShell";
import { BiFilterBar, useBiFilters } from "@/components/bi/BiFilters";
import {
  ChartCard,
  ChartState,
  MetricCard,
  formatMoneyCLP,
  formatNumber,
  tooltipStyle,
} from "@/components/bi/BiPrimitives";
import { estadoLabels } from "@/lib/mock-data";
import { biAnalyticsService } from "@/services/biAnalytics.service";

export const Route = createFileRoute("/bi/ia")({
  head: () => ({ meta: [{ title: "Reporte Inteligencia IA — LicitaPro" }] }),
  component: Page,
});

function Page() {
  const { filters } = useBiFilters();
  const scoring = useQuery({
    queryKey: ["bi", "scoring", filters],
    queryFn: () => biAnalyticsService.scoring(filters),
  });
  const s = scoring.data;
  const coverage = s && s.scored + s.pending > 0
    ? Math.round((s.scored / (s.scored + s.pending)) * 1000) / 10
    : null;

  return (
    <AppShell
      title="Inteligencia y scoring"
      subtitle="Compatibilidad calculada por el Motor IA sobre tus oportunidades"
      actions={
        <Link
          to="/bi"
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-border hover:bg-muted text-muted-foreground hover:text-foreground transition"
        >
          <ArrowLeft className="size-3.5" /> Reportes
        </Link>
      }
    >
      <BiFilterBar />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <MetricCard
          label="Oportunidades evaluadas"
          value={formatNumber(s?.scored ?? 0)}
          loading={scoring.isLoading}
        />
        <MetricCard
          label="Pendientes de evaluar"
          value={formatNumber(s?.pending ?? 0)}
          loading={scoring.isLoading}
        />
        <MetricCard
          label="Cobertura del motor"
          value={coverage != null ? `${coverage}%` : "—"}
          loading={scoring.isLoading}
        />
        <MetricCard
          label="Score promedio"
          value={s?.average != null ? s.average : "—"}
          loading={scoring.isLoading}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
        <ChartCard title="Distribución de compatibilidad" subtitle="Oportunidades por banda de score">
          <ChartState
            loading={scoring.isLoading}
            error={scoring.error}
            empty={!s?.distribution?.some((d) => d.count > 0)}
          >
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={s?.distribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                <XAxis
                  dataKey="band"
                  stroke="var(--color-muted-foreground)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="var(--color-muted-foreground)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" name="Oportunidades" fill="var(--color-chart-1)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartState>
        </ChartCard>

        <ChartCard title="Compatibilidad por categoría" subtitle="Score promedio del Motor IA">
          <ChartState
            loading={scoring.isLoading}
            error={scoring.error}
            empty={!s?.byCategory?.length}
          >
            <ResponsiveContainer width="100%" height={280}>
              <RadarChart data={s?.byCategory}>
                <PolarGrid stroke="var(--color-border)" />
                <PolarAngleAxis dataKey="name" stroke="var(--color-muted-foreground)" fontSize={10} />
                <Tooltip contentStyle={tooltipStyle} />
                <Radar
                  dataKey="score"
                  name="Score"
                  stroke="var(--color-chart-1)"
                  fill="var(--color-chart-1)"
                  fillOpacity={0.3}
                  strokeWidth={2}
                />
              </RadarChart>
            </ResponsiveContainer>
          </ChartState>
        </ChartCard>
      </div>

      <ChartCard title="Mejores oportunidades" subtitle="Top 10 por score de compatibilidad">
        <ChartState loading={scoring.isLoading} error={scoring.error} empty={!s?.top?.length} height={200}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground border-b border-border">
                  <th className="text-left font-medium py-2">Licitación</th>
                  <th className="text-left font-medium py-2">Organismo</th>
                  <th className="text-left font-medium py-2">Etapa</th>
                  <th className="text-right font-medium py-2">Monto</th>
                  <th className="text-right font-medium py-2">Score</th>
                </tr>
              </thead>
              <tbody>
                {(s?.top ?? []).map((row) => (
                  <tr key={row.id} className="border-b border-border/60 last:border-0">
                    <td className="py-2 max-w-[280px]">
                      <Link
                        to={row.tipo === "compra-agil" ? "/compra-agil/$id" : "/licitaciones/$id"}
                        params={{ id: row.id }}
                        className="text-foreground hover:text-primary truncate block"
                      >
                        {row.nombre}
                      </Link>
                      <span className="text-xs text-muted-foreground font-mono">{row.codigo}</span>
                      {row.tipo === "compra-agil" && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                          Compra Ágil
                        </span>
                      )}
                    </td>
                    <td className="py-2 text-muted-foreground truncate max-w-[200px]">{row.organismo}</td>
                    <td className="py-2 text-muted-foreground">
                      {(estadoLabels as Record<string, string>)[row.estadoPipeline] ?? row.estadoPipeline}
                    </td>
                    <td className="py-2 text-right tabular-nums text-foreground">
                      {row.montoEstimado != null && row.moneda === "CLP"
                        ? formatMoneyCLP(row.montoEstimado)
                        : row.montoEstimado != null
                          ? `${formatNumber(row.montoEstimado)} ${row.moneda}`
                          : "—"}
                    </td>
                    <td className="py-2 text-right tabular-nums font-semibold text-primary">{row.score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartState>
      </ChartCard>
    </AppShell>
  );
}
